/** @jsxImportSource @emotion/react */
'use client';

import React, { useId } from 'react';
import { Box, HiddenInput, LabelText, Root } from './styled';
import type { CheckboxProps } from './types';

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      variant = 'default',
      size = 'md',
      checked,
      defaultChecked,
      disabled = false,
      label,
      children,
      className,
      testId = 'checkbox',
      css,
      style,
      onChange,
      onCheckedChange,
      id,
      ...rest
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const isControlled = checked !== undefined;
    const [internalChecked, setInternalChecked] = React.useState(
      Boolean(defaultChecked)
    );
    const isChecked = isControlled ? Boolean(checked) : internalChecked;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setInternalChecked(e.target.checked);
      }
      onChange?.(e);
      onCheckedChange?.(e.target.checked);
    };

    return (
      <Root
        htmlFor={inputId}
        $variant={variant}
        $checked={isChecked}
        $disabled={disabled}
        className={className}
        css={css}
        style={style}
        data-testid={testId}
        data-variant={variant}
        data-checked={isChecked || undefined}
      >
        <HiddenInput
          ref={ref}
          id={inputId}
          type="checkbox"
          checked={isControlled ? checked : undefined}
          defaultChecked={!isControlled ? defaultChecked : undefined}
          disabled={disabled}
          onChange={handleChange}
          {...rest}
        />
        <Box $size={size} $checked={isChecked} $disabled={disabled} aria-hidden />
        {label || children ? <LabelText>{label ?? children}</LabelText> : null}
      </Root>
    );
  }
);

Checkbox.displayName = 'Checkbox';
